import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import { Tooltip } from '../tooltip'

import CounterElement from './counter-element'

import styles from './text-tab.module.css'

/**
 * @private
 * @param {string} title
 * @param {number} count
 * @param {string} testClassNamePostfix
 * @returns {Array<ReactElement>}
 */
function renderTextWithCounter(title, count, testClassNamePostfix) {
    return [
        <div key="text" className={cx(styles['text'], styles['text-with-counter'])}>
            {title}
        </div>,
        <CounterElement
            key="counter"
            count={count}
            className={styles['counter']}
            testClassNamePostfix={testClassNamePostfix}
        />
    ]
}

/**
 * @private
 * @param {string} title
 * @returns {Array<ReactElement>}
 */
function renderTextWithoutCounter(title) {
    return [
        <div key="text" className={cx(styles['text'], styles['text-without-counter'])}>
            {title}
        </div>
    ]
}

const propTypes = {
    activeClassName: PropTypes.string,

    className: PropTypes.string,

    count: PropTypes.number,
    // Don't remove the 'id' property, because it is needed by the TabBar.
    id: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number, PropTypes.func]).isRequired,
    isGreedy: PropTypes.bool,
    isHighlighted: PropTypes.bool,
    onBlur: PropTypes.func,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,
    onMouseDown: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onTouchStart: PropTypes.func,
    testClassNamePostfix: PropTypes.string,
    title: PropTypes.string,
    widthInPx: PropTypes.number,
    toolTipContent: PropTypes.string
}

const defaultProps = {
    count: 0,
    isGreedy: false,
    isHighlighted: false,
    testClassNamePostfix: '',
    title: '',
    widthInPx: null // means, no width
}

/**
 * Shows a menu tab which consists of a title text and a counter and
 * uses the {@link CounterElement} to render the counter value.
 *
 * <b>Hints:</b>
 * How to get a truncated text in CSS flexbox layout.
 * https://css-tricks.com/flexbox-truncated-text/
 *
 * @param {Object} props - The React properties object
 * @param {Object|Function|string|number} props.id - The current text tab id for {@link TabBar}
 * @param {string} [props.activeClassName] - class name for custom highlight styling
 * @param {string} [props.className] - Deprecated, use 'maxWidth' or 'isGreedy' instead
 * @param {number} [props.count=0] - The number to be shown
 * @param {boolean} [props.isGreedy=false] - 'true', if the TextTab should take all the space he can get
 * @param {boolean} [props.isHighlighted=false] - 'true', if the content is highlighted
 * @param {Function} [props.onBlur] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {Function} [props.onClick] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {Function} [props.onFocus] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {Function} [props.onMouseDown] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {Function} [props.onMouseEnter] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {Function} [props.onMouseLeave] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {Function} [props.onTouchStart] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {string} [props.testClassNamePostfix=''] - A class name postfix for tests only
 * @param {string} [props.title=''] - The text to be shown
 * @param {number} [props.widthInPx=0] - The maximum width (in pixel) the TextTab can take
 * @returns {ReactElement}
 */
function TextTab(props) {
    const {
        activeClassName,
        className,
        count,
        isGreedy,
        isHighlighted,
        onBlur,
        onClick,
        onFocus,
        onMouseDown,
        onMouseEnter,
        onMouseLeave,
        onTouchStart,
        testClassNamePostfix,
        title,
        widthInPx
    } = props

    const createCombinedClassName = () => {
        const highlightingStyle = { [cx(styles['highlighted'], activeClassName)]: isHighlighted }
        const testClassName = ['t3sel-text-tab', testClassNamePostfix].filter(Boolean).join('-')
        return cx(styles['tab'], highlightingStyle, { [styles['is-greedy']]: isGreedy }, className, testClassName)
    }

    const style = widthInPx ? { flexBasis: `${widthInPx}px` } : {}
    const textTabProps = {
        className: createCombinedClassName(),
        onBlur,
        onClick,
        onFocus,
        onMouseDown,
        onMouseEnter,
        onMouseLeave,
        onTouchStart,
        style
    }
    const tab = (
        <div {...textTabProps}>
            <div className={styles['content']}>
                {count > 0
                    ? renderTextWithCounter(title, count, testClassNamePostfix)
                    : renderTextWithoutCounter(title)}
            </div>
        </div>
    )

    return <Tooltip content={props.toolTipContent ?? props.title}>{tab}</Tooltip>
}

TextTab.propTypes = propTypes
TextTab.defaultProps = defaultProps

export default TextTab
